import { Link } from 'react-router';

import { useAdmin } from '@/app/admin-session';
import { canAccessAdminPath } from '@/components/admin/nav';
import { DetailSection, DocumentPreview, Field, FieldGrid } from '@/components/admin/detail-parts';
import { VideoPlayer } from '@/components/admin/video-player';
import { StatusBadge } from '@/components/ui/status-badge';
import { DOCUMENT_STATUS, type DocumentStatus, type ModerationItem } from '@/contracts';
import { useTranslations } from '@/i18n/use-locale';
import { isoDate } from '@/lib/detail';
import { formatBytes, formatDuration } from '@/lib/format';
import {
  useCredentialKindLabel,
  useDocumentStatusLabel,
  useModerationDocumentKindLabel,
} from '@/lib/labels';
import { documentTone } from '@/lib/status';
import { ModerationDecision } from './moderation-decision';
import { Card } from '@/components/ui/card';
import { HistorySection } from './moderation-history-section';
import { useVideoDecisionWording } from './use-video-wording';

function documentStatus(item: ModerationItem): DocumentStatus {
  if (item.status === 'approved') return DOCUMENT_STATUS.APPROVED;
  if (item.status === 'rejected') return DOCUMENT_STATUS.REJECTED;
  return DOCUMENT_STATUS.AWAITING_AUDIT;
}

/**
 * One talent document under review: a credential attachment beside the item it backs,
 * or a video introduction played in place — it backs no item, so its facts are the
 * running time and the type the worker read from the bytes.
 */
export function DocumentReview({
  item,
  onDecided,
}: {
  item: ModerationItem;
  onDecided: () => void;
}) {
  const t = useTranslations('admin.review');
  const documents = useTranslations('admin.documents');
  const kindLabel = useCredentialKindLabel();
  const documentKindLabel = useModerationDocumentKindLabel();
  const statusLabel = useDocumentStatusLabel();
  const videoWording = useVideoDecisionWording();
  const admin = useAdmin();
  const doc = item.document;

  if (!doc) return null;

  const status = documentStatus(item);
  const canOpenTalent = canAccessAdminPath('/talents', admin.permissions);
  const candidateName = doc.candidateName || documents('unknownCandidate');
  const isVideo = doc.kind === 'video_intro';
  const title = isVideo ? documentKindLabel(doc.kind) : (doc.credentialTitle ?? '');
  const kind = isVideo ? title : doc.credentialKind ? kindLabel(doc.credentialKind) : '';

  return (
    <>
      <Card asChild>
        <header className="p-5 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-navy" dir="auto">
                {isVideo ? `${title} — ${candidateName}` : title}
              </h2>
              <p className="mt-0.5 text-[12px] text-conc-500">
                {kind}
                {' · '}
                <span className="num">
                  {t('submittedOn', { date: isoDate(item.submittedAt) ?? '' })}
                </span>
              </p>
            </div>
            <StatusBadge tone={documentTone(status)}>{statusLabel(status)}</StatusBadge>
          </div>
        </header>
      </Card>

      <DetailSection title={isVideo ? title : t('section.document')}>
        <FieldGrid>
          <Field label={t('field.candidate')}>
            {canOpenTalent ? (
              <Link
                to={`/talents/${doc.candidateId}`}
                className="text-navy hover:text-pitch hover:underline"
              >
                {candidateName}
              </Link>
            ) : (
              candidateName
            )}
          </Field>
          {isVideo ? (
            <>
              <Field label={t('field.kind')}>{kind}</Field>
              <Field label={t('field.duration')}>
                <span className="num">
                  {doc.durationSeconds !== undefined
                    ? formatDuration(doc.durationSeconds)
                    : documents('video.noDuration')}
                </span>
              </Field>
              <Field label={t('field.mimeType')}>
                {doc.mimeType && <span className="ltr-field">{doc.mimeType}</span>}
              </Field>
            </>
          ) : (
            <>
              <Field label={t('field.credential')}>{doc.credentialTitle}</Field>
              <Field label={t('field.kind')}>{kind}</Field>
              <Field label={t('field.issuer')}>{doc.issuer || documents('unknownIssuer')}</Field>
            </>
          )}
          <Field label={t('field.filename')}>
            {doc.filename && <span className="ltr-field">{doc.filename}</span>}
          </Field>
          <Field label={t('field.size')}>
            {doc.sizeBytes ? <span className="num">{formatBytes(doc.sizeBytes)}</span> : null}
          </Field>
        </FieldGrid>
      </DetailSection>

      <DetailSection title={isVideo ? t('section.video') : t('section.file')}>
        {item.previewUrl ? (
          isVideo ? (
            <VideoPlayer
              src={item.previewUrl}
              label={documents('video.player', { name: candidateName })}
              className="block max-h-[560px] w-full max-w-md"
            />
          ) : (
            <DocumentPreview url={item.previewUrl} title={documents('previewAlt', { title })} />
          )
        ) : (
          <p className="text-[12.5px] text-conc-400">
            {isVideo ? documents('video.noPreview') : documents('noPreview')}
          </p>
        )}
      </DetailSection>

      <HistorySection item={item} />

      {status === DOCUMENT_STATUS.AWAITING_AUDIT ? (
        <DetailSection title={t('section.decision')}>
          <ModerationDecision
            item={item}
            name={isVideo ? candidateName : title}
            onDecided={onDecided}
            wording={isVideo ? videoWording(candidateName) : undefined}
          />
        </DetailSection>
      ) : null}
    </>
  );
}
